import type { ComponentProps } from "react";
import style from "./style.module.css";
import DesignSizeOption from "./DesignSizeOption";

// DesignSizeOption is already typed and owns the shape it reads from a design.
type DesignSizeOptionDesign = ComponentProps<typeof DesignSizeOption>["design"];

/**
 * The design type stays generic: this component only reads `id`, and it hands
 * whole designs back to the caller's own callbacks, which are written against
 * the caller's richer design type.
 */
type CategoryDesign = DesignSizeOptionDesign & { id: string };

interface DesignSizeCategory<D> {
  name: string;
  designs: D[];
}

interface DesignSizeOptionsProps<D> {
  category: DesignSizeCategory<D>;
  handleChangePreview: (design: D) => void;
  isHighlighted: (design: D) => boolean;
}

export const DesignSizeOptions = <D extends CategoryDesign>({
  category,
  handleChangePreview,
  isHighlighted
}: DesignSizeOptionsProps<D>) => {
  return (
    <div className={style.designSizeOptions} data-testid="DesignSizeOptions">
      {category.designs.map(design => (
        <DesignSizeOption
          design={design}
          isHighlighted={isHighlighted(design)}
          onClick={() => handleChangePreview(design)}
          key={design.id}
        />
      ))}
    </div>
  );
};

DesignSizeOptions.displayName = "DesignSizeOptions";

interface DesignSizeCategoriesProps<D> {
  collectionDesignsCategorised: DesignSizeCategory<D>[];
  handleChangePreview: (design: D) => void;
  isHighlighted: (design: D) => boolean;
}

export const DesignSizeCategories = <D extends CategoryDesign>({
  collectionDesignsCategorised,
  handleChangePreview,
  isHighlighted
}: DesignSizeCategoriesProps<D>) => {
  return (
    <div className={style.sizeCategories} data-testid="DesignSizeCategories">
      {collectionDesignsCategorised.map(category => (
        <div className={style.sizeCategory} key={category.name}>
          <div className={style.sizeCategoryTitle}>{category.name}</div>
          <DesignSizeOptions
            category={category}
            handleChangePreview={handleChangePreview}
            isHighlighted={isHighlighted}
          />
        </div>
      ))}
    </div>
  );
};

DesignSizeCategories.displayName = "DesignSizeCategories";

export default DesignSizeCategories;
